import { Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MessageService } from '../../services/message.service';
import { MessageDto } from '../../models/api/messageDto';
import { LinkifyPipe } from '../../pipes/linkify.pipe';

@Component({
  selector: 'app-notifications-menu',
  templateUrl: './notifications-menu.component.html',
  styleUrls: ['./notifications-menu.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    LinkifyPipe
  ]
})
export class NotificationsMenuComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  messages = signal<MessageDto[]>([]);
  unreadCount = this.messageService.unreadCount;

  ngOnInit(): void {
    this.loadMessages();
  }

  refresh(): void {
    this.loadMessages();
  }

  private loadMessages(): void {
    this.messageService.getMessages(1, 5).subscribe({
      next: (response) => this.messages.set(response.data),
      error: () => this.messages.set([])
    });
  }

  markAllAsRead(): void {
    const unreadMessages = this.messages().filter(m => !m.isRead);
    if (unreadMessages.length === 0) return;

    let completed = 0;
    for (const message of unreadMessages) {
      this.messageService.updateReadStatus(message.id, { isRead: true }).subscribe({
        next: () => {
          completed++;
          if (completed === unreadMessages.length) {
            this.loadMessages();
            this.messageService.refreshUnreadCount();
          }
        }
      });
    }
  }

  markAsRead(message: MessageDto, event: MouseEvent): void {
    // Clicking the envelope icon marks the message read without opening it,
    // so stop the row's openMessage from firing.
    event.stopPropagation();
    if (message.isRead) return;

    // Unlike openMessage (where navigation persists the read state server-side),
    // we aren't navigating, so persist it ourselves.
    this.messageService.updateReadStatus(message.id, { isRead: true }).subscribe({
      next: () => {
        this.messages.update(msgs =>
          msgs.map(m => m.id === message.id ? { ...m, isRead: true } : m)
        );
        this.messageService.refreshUnreadCount();
      }
    });
  }

  openMessage(message: MessageDto): void {
    if (!message.isRead) {
      this.messages.update(msgs =>
        msgs.map(m => m.id === message.id ? { ...m, isRead: true } : m)
      );
      this.messageService.unreadCount.update(count => Math.max(0, count - 1));
    }

    if (message.isAnswer) {
      this.router.navigate(['/messages', message.parentMessageId], {
        queryParams: { answerId: message.id }
      });
    } else {
      this.router.navigate(['/messages', message.id], {
        queryParams: { originalOnly: true }
      });
    }
  }

  onSeeAllNotifications(): void {
    this.router.navigate(['/messages']);
  }
}
